import React from 'react';
import { Link } from 'react-router-dom';
import HrnbButton from '../controls/HrnbButton';
import HrnbCard from '../controls/HrnbCard';
import { DetailsIcon, TrophyIcon, RegisterIcon, RejectIcon } from '../../icons';
import './AnnualTournamentCard.css';

/**
 * Карточка ежегодного турнира
 * @param {Object} props
 * @param {Object} props.tournament - Данные турнира
 * @param {Object} props.currentUser - Текущий пользователь
 * @param {Function} props.onRegister - Обработчик регистрации
 * @param {Function} props.onUnregister - Обработчик отмены регистрации
 * @param {boolean} props.registering - Статус регистрации
 */
function AnnualTournamentCard({ 
    tournament, 
    currentUser, 
    onRegister, 
    onUnregister,
    registering 
}) {
    const {
        id,
        year,
        title,
        description,
        status,
        startDate,
        endDate,
        registrationStart,
        registrationEnd,
        prizePoolAmount,
        currency,
        participantCount,
        maxParticipants,
        coverUrl,
        winnerName,
        currentStage
    } = tournament;

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Статусы
    const statusLabels = {
        REGISTRATION: 'Регистрация',
        UPCOMING: 'Скоро начнётся',
        ACTIVE: 'Активен',
        COMPLETED: 'Завершён',
        CANCELLED: 'Отменён'
    };

    const statusColors = {
        REGISTRATION: 'success',
        UPCOMING: 'info',
        ACTIVE: 'primary',
        COMPLETED: 'secondary',
        CANCELLED: 'danger'
    };

    // Проверка возможности регистрации
    const canRegister = () => {
        if (status !== 'REGISTRATION') return false;
        if (!currentUser) return false;
        if (participantCount >= maxParticipants) return false;
        
        const now = new Date();
        const regEnd = registrationEnd ? new Date(registrationEnd) : null;
        return !regEnd || now < regEnd;
    };

    // Проверка возможности отмены регистрации
    const canUnregister = () => {
        if (status !== 'REGISTRATION' && status !== 'UPCOMING') return false;
        if (!currentUser) return false;
        
        // Предполагаем, что пользователь зарегистрирован, если participantCount > 0
        // В реальном приложении нужно проверять через API
        return true;
    };

    const isRegistered = currentUser && participantCount > 0; // Упрощённая проверка

    return (
        <HrnbCard className="annual-tournament-card">
            {/* Обложка */}
            {coverUrl && (
                <div className="annual-tournament-card__cover">
                    <img 
                        src={coverUrl} 
                        alt={title}
                        className="annual-tournament-card__image"
                    />
                    {status === 'ACTIVE' && (
                        <div className="annual-tournament-card__badge annual-tournament-card__badge--active">
                            Активен
                        </div>
                    )}
                </div>
            )}

            <div className="annual-tournament-card__body">
                {/* Заголовок */}
                <div className="annual-tournament-card__header">
                    <h3 className="annual-tournament-card__title">
                        {title}
                    </h3>
                    <span className={`annual-tournament-card__status annual-tournament-card__status--${statusColors[status] || 'secondary'}`}>
                        {statusLabels[status] || status}
                    </span>
                </div>

                {/* Описание */}
                {description && (
                    <p className="annual-tournament-card__description">
                        {description}
                    </p>
                )}

                {/* Информация */}
                <div className="annual-tournament-card__info">
                    <div className="annual-tournament-card__info-row">
                        <span className="annual-tournament-card__label">Год:</span>
                        <span className="annual-tournament-card__value">{year}</span>
                    </div>
                    
                    {startDate && (
                        <div className="annual-tournament-card__info-row">
                            <span className="annual-tournament-card__label">Начало:</span>
                            <span className="annual-tournament-card__value">
                                {formatDate(startDate)}
                            </span>
                        </div>
                    )}
                    
                    {endDate && (
                        <div className="annual-tournament-card__info-row">
                            <span className="annual-tournament-card__label">Окончание:</span>
                            <span className="annual-tournament-card__value">
                                {formatDate(endDate)}
                            </span>
                        </div>
                    )}

                    {registrationStart && (
                        <div className="annual-tournament-card__info-row">
                            <span className="annual-tournament-card__label">Регистрация:</span>
                            <span className="annual-tournament-card__value">
                                {formatDate(registrationStart)} — {formatDate(registrationEnd)}
                            </span>
                        </div>
                    )}

                    {prizePoolAmount && (
                        <div className="annual-tournament-card__info-row">
                            <span className="annual-tournament-card__label">Призовой фонд:</span>
                            <span className="annual-tournament-card__value annual-tournament-card__value--prize">
                                {prizePoolAmount.toLocaleString('ru-RU')} {currency}
                            </span>
                        </div>
                    )}

                    <div className="annual-tournament-card__info-row">
                        <span className="annual-tournament-card__label">Участники:</span>
                        <span className="annual-tournament-card__value">
                            {participantCount} из {maxParticipants}
                        </span>
                    </div>

                    {/* Прогресс бар участников */}
                    <div className="annual-tournament-card__progress">
                        <div 
                            className="annual-tournament-card__progress-bar"
                            style={{ width: `${(participantCount / maxParticipants) * 100}%` }}
                        />
                    </div>

                    {currentStage && status === 'ACTIVE' && (
                        <div className="annual-tournament-card__info-row">
                            <span className="annual-tournament-card__label">Текущая стадия:</span>
                            <span className="annual-tournament-card__value">
                                {currentStage === 'QUALIFICATION' && 'Квалификация'}
                                {currentStage === 'ROUND_OF_64' && '1/64 финала'}
                                {currentStage === 'ROUND_OF_32' && '1/32 финала'}
                                {currentStage === 'ROUND_OF_16' && '1/16 финала'}
                                {currentStage === 'QUARTER_FINALS' && 'Четвертьфиналы'}
                                {currentStage === 'SEMI_FINALS' && 'Полуфиналы'}
                                {currentStage === 'FINALS' && 'Финал'}
                            </span>
                        </div>
                    )}

                    {winnerName && (
                        <div className="annual-tournament-card__info-row">
                            <span className="annual-tournament-card__label">Победитель:</span>
                            <span className="annual-tournament-card__value annual-tournament-card__value--winner">
                                🏆 {winnerName}
                            </span>
                        </div>
                    )}
                </div>

                {/* Действия */}
                <div className="annual-tournament-card__actions">
                    <Link to={`/tournaments/annual/${id}`}>
                        <HrnbButton variant="secondary" size="sm" icon={<DetailsIcon />}>
                            Подробнее
                        </HrnbButton>
                    </Link>

                    {status === 'ACTIVE' && (
                        <Link to={`/tournaments/annual/${id}/bracket`}>
                            <HrnbButton variant="primary" size="sm" icon={<TrophyIcon />}>
                                Сетка турнира
                            </HrnbButton>
                        </Link>
                    )}

                    {canRegister() && !isRegistered && (
                        <HrnbButton 
                            variant="primary" 
                            size="sm"
                            onClick={onRegister}
                            disabled={registering}
                            icon={<RegisterIcon />}
                        >
                            {registering ? 'Регистрация...' : 'Зарегистрироваться'}
                        </HrnbButton>
                    )}

                    {canUnregister() && isRegistered && (
                        <HrnbButton 
                            variant="danger" 
                            size="sm"
                            onClick={onUnregister}
                            disabled={registering}
                            icon={<RejectIcon />}
                        >
                            Отменить регистрацию
                        </HrnbButton>
                    )}
                </div>
            </div>
        </HrnbCard>
    );
}

export default AnnualTournamentCard;
